import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AdminDocumentListItem,
  AdminDocumentUploadDto,
  AdminDocumentsFilter,
} from '@models/resources/admin-document.model';
import { DocumentClassification } from '@models/resources/document.model';

/**
 * Patch-Shape für `AdminDocumentsFacade.update()` (Sprint 6 Post-Sprint-
 * Polish). Matched `PUT /api/documents/{id}` 1:1 — alle drei Felder
 * `sometimes`-validated im BE.
 */
export interface AdminDocumentUpdateDto {
  readonly title?: string;
  readonly classification?: DocumentClassification;
  readonly visible_to_customer?: boolean;
}

/**
 * Staff-seitige Dokumentenverwaltung (Sprint 3, S3-6 Teil 3).
 *
 * Zwei konkrete Implementierungen:
 * - `DummyAdminDocumentsFacade` – teilt Fixtures mit Customer-Facade,
 *   listet **alle** Einträge (auch `visible_to_customer: false`) und
 *   wrapt sie mit eager-loaded Kundenfeldern.
 * - `HttpAdminDocumentsFacade` – Stub bis Sprint 5.
 *
 * Method-Mapping (Technisches Konzept §4.2.5):
 * - `list(filter)`          → `GET /api/documents`
 * - `upload(dto)`           → `POST /api/documents` (multipart)
 * - `toggleVisibility(id)`  → `PATCH /api/documents/{id}/visibility`
 * - `delete(id)`            → `DELETE /api/documents/{id}`
 * - `downloadBatch(ids)`    → `POST /api/documents/download-batch` (ZIP)
 */
/**
 * Bulk-Upload-Resultat (Sprint 6 Polish S6-19). Best-Effort-Pattern:
 * `succeeded` enthält die erfolgreich erstellten Documents, `failed`
 * die Filenamen mit der Fehlermeldung. Caller rendert einen Toast
 * mit der Summe, plus optional eine detail-Liste der Failures.
 */
export interface BulkUploadResult {
  readonly succeeded: readonly AdminDocumentListItem[];
  readonly failed: readonly { filename: string; reason: string }[];
}

export interface AdminDocumentsFacade {
  list(filter?: AdminDocumentsFilter): Observable<AdminDocumentListItem[]>;
  upload(dto: AdminDocumentUploadDto): Observable<AdminDocumentListItem>;
  /**
   * Sequential Multi-File-Upload (Sprint 6 Polish S6-19). Ruft `upload`
   * pro File einzeln; Server-Errors landen im `failed[]`. Caller liefert
   * pro File ein vollständiges Upload-DTO (= Title aus File-Name oder
   * Form-Input, Customer/Type/Visibility gemeinsam).
   */
  bulkUpload(dtos: readonly AdminDocumentUploadDto[]): Observable<BulkUploadResult>;
  /**
   * Patch-API (Sprint 6 S6-17, BE `PUT /api/documents/{id}`). Felder
   * sind alle optional; nur das Übergebene wird patched. Schreibt
   * `metadata_changed` oder `visibility_changed` Audit-Events
   * (BE entscheidet basierend auf `touchedKeys`).
   */
  update(id: number, dto: AdminDocumentUpdateDto): Observable<AdminDocumentListItem>;
  toggleVisibility(id: number): Observable<AdminDocumentListItem>;
  delete(id: number): Observable<void>;
  /**
   * Einzeldownload eines Dokuments aus der Admin-Oberfläche.
   * → `GET /api/documents/{id}/download`. Staff-Zugriff laut
   * `DocumentDownloadController` immer erlaubt (Customer nur eigene,
   * nur `visible_to_customer = true`).
   */
  download(id: number): Observable<Blob>;
  /**
   * Massen-Download mehrerer Dokumente als ZIP
   * (Technisches Konzept §4.2.5 + §5.9 `DocumentBatchDownloadComponent`).
   * → `POST /api/documents/batch-download` mit `{ ids: number[] }`
   * (S7-24, ZIP-Blob). Im Dummy-Modus liefert die Methode einen leeren
   * Blob zurück, damit der UI-Flow end-to-end testbar ist.
   */
  downloadBatch(ids: readonly number[]): Observable<Blob>;
}

export const ADMIN_DOCUMENTS_FACADE = new InjectionToken<AdminDocumentsFacade>(
  'AdminDocumentsFacade',
);
